import { useBunja } from 'bunja/react';
import { useAtomValue, useSetAtom, useStore } from 'jotai';
import { useTranslations } from 'next-intl';
import { useCallback } from 'react';

import { FormattedMessage } from '@flipster/i18n';
import { createDropdownSelect } from '@flipster/react/design-system/dropdown/dropdown-select';
import ChevronDownIcon from '@flipster/react/icon/generated/ChevronDownIcon';
import { css } from '@flipster/styled-system/css';
import { hstack } from '@flipster/styled-system/patterns';
import { Price } from '@flipster/ui';
import { UsdtCurrency, getQuoteCurrencyPrecision } from '@flipster/utils/constants/currency';
import segment from '@flipster/utils/segment';
import { TradeEvent } from '@flipster/utils/segment/events/trade';

import { tradeSymbolBunja } from '@web/app/[locale]/(web)/trade/_misc/symbol';

import { formBunja } from './bunja/form';
import { formSegmentDataBunja } from './bunja/form-segment-data';
import { labelWrapper } from './styles';

const Dropdown = createDropdownSelect();
const orderTypesLabel = {
  margin: <FormattedMessage id='Trade.r250401.Margin' />,
  positionSize: <FormattedMessage id='Trade.r250401.Position_size' />,
};

/**
 * 주문 타입 드롭다운(margin/positionSize) + 가용 잔고 표시.
 * kind·availableFunds·symbolInfo에만 의존하므로 수량 keystroke에는 리렌더되지 않는다.
 * segment 페이로드용 값(positionMode/orderType)은 표시값이 아니므로 store.get으로 읽는다.
 */
const OrderTypeHeader = () => {
  const transTrade = useTranslations('Trade.r250401');
  const store = useStore();
  const { symbolInfoWithDefaultAtom } = useBunja(tradeSymbolBunja);
  const { availableFundsAtom, transitKindAtom, kindAtom } = useBunja(formBunja);
  const { symbol, orderTypeAtom, positionModeAtom } = useBunja(formSegmentDataBunja);

  const symbolInfo = useAtomValue(symbolInfoWithDefaultAtom);
  const availableFunds = useAtomValue(availableFundsAtom);
  const kind = useAtomValue(kindAtom);
  const transitKind = useSetAtom(transitKindAtom);

  const quoteCurrency = symbolInfo?.quoteCurrency ?? UsdtCurrency;
  const quoteCurrencyPrecision = getQuoteCurrencyPrecision(quoteCurrency);
  const orderTypeForm = kind === 'margin' ? 'margin' : 'positionSize';

  const onChangeOrderTypeForm = useCallback(
    (val: string | undefined) => {
      if (val === 'margin') transitKind('margin');
      if (val === 'positionSize') transitKind('quantity');
    },
    [transitKind],
  );

  return (
    <div className={labelWrapper}>
      <Dropdown.Root
        selected={orderTypeForm}
        onChange={(val) => {
          onChangeOrderTypeForm(val);
          segment.track(TradeEvent.OrderFormUnitSwitchClick, {
            trade_type: 'perpetual',
            symbol: symbol,
            position_mode: store.get(positionModeAtom),
            order_by_token: val === 'margin' ? 'margin' : kind === 'quantity' ? 'quantity' : 'position_size',
            order_type: store.get(orderTypeAtom),
          });
        }}
      >
        <Dropdown.Trigger css={hstack.raw({ gap: 4, color: 'content.default.level-4' })}>
          <p className={css({ textStyle: 'Text/L/Strong' })}>{orderTypesLabel[orderTypeForm]}</p>
          <ChevronDownIcon
            size={18}
            className={css({
              transition: 'transform 0.2s',
              '[aria-expanded=true] &': { transform: 'rotate(-180deg)' },
            })}
          />
        </Dropdown.Trigger>
        <Dropdown.Select portal>
          <Dropdown.Item value='positionSize' label={orderTypesLabel.positionSize} />
          <Dropdown.Item value='margin' label={orderTypesLabel.margin} />
        </Dropdown.Select>
      </Dropdown.Root>
      <div>
        <span className={css({ textStyle: 'Text/S/Regular', color: 'content.default.level-2', pr: 2 })}>
          {transTrade('Avbl_funds_colon')}
        </span>
        <span className={css({ textStyle: 'Text/S/Regular', color: 'content.default.level-5' })}>
          <Price
            fontType='num4'
            price={availableFunds}
            precision={quoteCurrencyPrecision}
            currency={quoteCurrency}
            toFixed
          />
        </span>
      </div>
    </div>
  );
};

export default OrderTypeHeader;
